import React, { useState } from 'react'
import {
  strategyBackdropWhiteSx,
  strategyBorderMutedSx,
  strategyCardPad20Sx,
  strategyDarkTextSx,
  strategyFlexAutoTopSx,
  strategyFlexGrowSx,
  strategyFont16Sx,
  strategyFontSemiboldSx,
  strategyFullWidthSx,
  strategyInlineFlexSx,
  strategyLinkBlueSx,
  strategyMarginTop25Sx,
  strategyMarginTop35Sx,
  strategyMetricRowFlexSx,
  strategyMinHeight50Sx,
  strategyMutedMetaSx,
  strategyMutedValueSx,
  strategyDescriptionTextSx,
  strategyOverflowVisibleCardSx,
  strategySummaryBandSx,
  strategyWhiteSurfaceSx,
  strategyPacemorbyCardSx,
  strategySemiboldMb10Sx,
  strategySemiboldMb20Sx,
  strategySummaryInsetSx,
  strategyWhiteBlueChipSx,
} from '@/housefolios-components/PropertyContainer/CenterPage/Common/strategyDashboardSx'
import {
  Grid,
  Card,
  CardHeader,
  Typography,
  IconButton,
  Tooltip,
  Button,
  Backdrop,
  CircularProgress,
  CardContent,
  Select,
  MenuItem,
  Divider,
  Box,
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import RemoveIcon from '@mui/icons-material/Remove'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'

import {
  map,
  filter,
  compact,
  includes,
  isEmpty,
  find,
  get,
  head,
  sum,
  truncate,
  padStart,
  isNil,
  toUpper,
} from '@/utils/lodash'
import ContactAgent from '../../../Marketplace/ContactAgent'
import DisplayRealeflowData from '@/housefolios-components/Dialogs/DisplayRealeflowData'
import FormattedDescription from '@/housefolios-components/shared/FormattedDescription'
import { percentageFormat } from '@/utils/number'
import CountUp from '@/utils/SafeCountUp'
import CommentsDialog from '@/housefolios-components/Dialogs/Comments'
import {
  ContactInformation,
  getCashEntryFee,
  getCreativeEntryFee,
  getPitiTotal,
} from '@/utils/loanBalance'
import SubtoPITICash from '@/housefolios-components/Dialogs/SubtoPITI/Cash/'
import SubtoPITICreative from '@/housefolios-components/Dialogs/SubtoPITI/Creative/'

const StatusBadge = ({ section  }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      py: '0.5rem',
    }}
  >
    <Box component="span" sx={{ fontWeight: 700 }}>
      {section.name}:
    </Box>
    <Box component="span" sx={[{
      display: "inline-block",
      py: 0,
      px: "0.7em",
      fontSize: "70%",
      fontWeight: 700,
      lineHeight: "21px",
      textAlign: "center",
      whiteSpace: "nowrap",
      verticalAlign: "baseline",
      borderRadius: "0.2rem",
      textTransform: "uppercase",
      height: 20,
    }, { color: "#fff", backgroundColor: "#1bc943" }]}>
      {section.formatter || '-'}
    </Box>
  </Box>
)

const SummaryDetail = ({ section  }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      py: '0.5rem',
    }}
  >
    <Box component="span" sx={{ fontWeight: 700 }}>
      {section.name}:
    </Box>
    <Box component="span" sx={strategyMutedValueSx}>
      {section.formatter
        ? section.formatter.format(section.value) || '-'
        : section.value || '-'}
    </Box>
  </Box>
)

const SummaryDetails = ({
  summaryDetails,
  strategyCards,
  property,
  preferences,
  currencyFormat,
  analysis,
}) => (
  <Box sx={[{ py: '1rem', borderRadius: '0.29rem' }, { fontSize: "0.875rem !important" }]}>
    {map(summaryDetails, (section) =>
      section.name !== 'Status' ? (
        <SummaryDetail key={section.name} section={section} />
      ) : (
        <StatusBadge key={section.name} section={section} />
      ),
    )}
    <Box sx={[strategySummaryBandSx, { fontSize: "0.875rem !important" }, strategySummaryInsetSx]}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Box component="span" sx={{ fontWeight: 700 }}>
          ARV:
        </Box>
        <Box component="span" sx={strategyMutedValueSx}>
          {head(
            compact(
              map(
                filter(strategyCards, (card) =>
                  !isEmpty(property.strategies)
                    ? includes(property.strategies, card.name)
                    : find(get(preferences, 'general.strategies'), {
                        value: card.name,
                        active: true,
                      }),
                ),
                (card) => card.afterRepairValue,
              ),
            ),
          )
            ? currencyFormat.format(
                head(
                  compact(
                    map(
                      filter(strategyCards, (card) =>
                        !isEmpty(property.strategies)
                          ? includes(property.strategies, card.name)
                          : find(
                              get(preferences, 'general.strategies'),
                              {
                                value: card.name,
                                active: true,
                              },
                            ),
                      ),
                      (card) => card.afterRepairValue,
                    ),
                  ),
                ),
              )
            : currencyFormat.format(0)}
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          py: '0.5rem',
        }}
      >
        <Box component="span" sx={{ fontWeight: 700 }}>
          Rent:
        </Box>
        <Box component="span" sx={strategyMutedValueSx}>
          {currencyFormat.format(sum(analysis.rent))}
        </Box>
      </Box>
    </Box>
  </Box>
)

const GorillaDetails = ({
  analysis,
  analytics,
  currencyFormat,
  numberFormat,
  gorillaSplit,
  gorillaFinalFee,
}) => (
  <Box sx={[strategySummaryBandSx, { fontSize: "1rem !important" }, strategySummaryInsetSx]}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
      <Box component="span" sx={{ fontWeight: 700 }}>
        Purchase Price:
      </Box>
      <Box component="span" sx={strategyMutedValueSx}>
        {currencyFormat.format(analysis.offerPrice)}
      </Box>
    </Box>
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        py: '0.5rem',
      }}
    >
      <Box component="span" sx={{ fontWeight: 700 }}>
        Renovation:
      </Box>
      <Box component="span" sx={strategyMutedValueSx}>
        {currencyFormat.format(analysis.rehabCost)}
      </Box>
    </Box>
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        py: '0.5rem',
      }}
    >
      <Box component="span" sx={{ fontWeight: 700 }}>
        Hold Time:
      </Box>
      <Box component="span" sx={strategyMutedValueSx}>
        {numberFormat.format(
          isNil(analysis.rehabDays)
            ? (analysis.rehabCost || 0) / 500
            : analysis.rehabDays,
        )}{' '}
        days
      </Box>
    </Box>
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        py: '0.5rem',
      }}
    >
      <Box component="span" sx={{ fontWeight: 700 }}>
        Sale Price:
      </Box>
      <Box component="span" sx={strategyMutedValueSx}>
        {currencyFormat.format(analysis.resellPrice)}
      </Box>
    </Box>
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        py: '0.5rem',
      }}
    >
      <Box component="span" sx={{ fontWeight: 700 }}>
        My Profit:
      </Box>
      <Box component="span" sx={strategyMutedValueSx}>
        {currencyFormat.format(
          analytics.fixFlip.saleProceeds * gorillaSplit <
            gorillaFinalFee
            ? analytics.fixFlip.saleProceeds - gorillaFinalFee
            : analytics.fixFlip.saleProceeds * (1 - gorillaSplit),
        )}
      </Box>
    </Box>
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        py: '0.5rem',
      }}
    >
      <Box component="span" sx={{ fontWeight: 700 }}>
        Gorilla Profit:
      </Box>
      <Box component="span" sx={strategyMutedValueSx}>
        {currencyFormat.format(
          analytics.fixFlip.saleProceeds * gorillaSplit <
            gorillaFinalFee
            ? 0
            : analytics.fixFlip.saleProceeds * gorillaSplit -
                gorillaFinalFee,
        )}
      </Box>
    </Box>
  </Box>
)

const PacemorbyDetails = ({
  pitiTotal,
  creativeEntryFee,
  cashEntryFee,
  percentageOfEntryFee,
}) => (
  <Box
    sx={[strategyCardPad20Sx, {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
    }]}
  >
    {/* Row 1: Entry Fees */}
    <Box
      sx={[strategyFullWidthSx, {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
      }]}
    >
      <Box
        sx={[strategyFlexGrowSx, {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }]}
      >
        <Typography
          variant="h5"
          sx={strategySemiboldMb10Sx}
        >
          Cash Entry Fee
        </Typography>
        <Typography variant="h4" sx={strategyFontSemiboldSx}>
          {cashEntryFee}
        </Typography>
      </Box>

      <Box
        sx={[strategyFlexGrowSx, {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }]}
      >
        <Typography
          variant="h5"
          sx={strategySemiboldMb10Sx}
        >
          Creative Entry Fee
        </Typography>
        <Typography variant="h4" sx={strategyFontSemiboldSx}>
          {creativeEntryFee}
        </Typography>
      </Box>
    </Box>

    {/* Row 2: PITI Total and % of Purchase Price */}
    <Box sx={[{ width: '100%', marginTop: '30px' }, {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
      }]}>
      <Box
        sx={[strategyFlexGrowSx, {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }]}
      >
        <Typography
          variant="h5"
          sx={strategySemiboldMb10Sx}
        >
          PITI Total
        </Typography>
        <Typography variant="h4" sx={strategyFontSemiboldSx}>
          {pitiTotal}
        </Typography>
      </Box>

      <Box
        sx={[strategyFlexGrowSx, {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }]}
      >
        <Typography
          variant="h5"
          sx={strategySemiboldMb10Sx}
        >
          % of Purchase Price
        </Typography>
        <Typography variant="h4" sx={strategyFontSemiboldSx}>
          {percentageOfEntryFee}
        </Typography>
      </Box>
    </Box>
  </Box>
)

const PITICard = ({ property, analysis, userId  }) => {
  const [dealSauceView, setDealSauceView] = useState('creative')
  const [openPITICashData, setOpenPITICashData] = useState(false)
  const [openPITICreativeData, setOpenPITICreativeData] =
    useState(false)

  const pitiTotal = getPitiTotal(property)
  const creativeEntryFee = getCreativeEntryFee(property)
  const cashEntryFee = getCashEntryFee(property)

  return (
    <Card variant="outlined" sx={{ width: '100%' }}>
      <Box sx={strategyMetricRowFlexSx}>
        <Box sx={{
          flexGrow: 1,
          minWidth: 0,
          "& > b": { display: "block" },
          "& > small": {
            textTransform: "uppercase",
            display: "block",
            opacity: 0.3,
            fontWeight: "bold",
          },
        }}>
          <b>Deal Sauce Summary</b>
        </Box>
        <Select
          value={dealSauceView}
          name="dealSauceView"
          onChange={(event) => setDealSauceView(event.target.value)}
          sx={strategyFullWidthSx}
        >
          <MenuItem value="cash" key="cash">
            Cash
          </MenuItem>
          <MenuItem value="creative" key="creative">
            Creative
          </MenuItem>
        </Select>
      </Box>
      <Divider />
      <CardContent>
        {dealSauceView === 'cash' && (
          <>
            <Box sx={[strategySummaryBandSx, { borderRadius: '0.65rem', p: '0.5rem', mb: '0.5rem', textAlign: 'center' }, { borderWidth: "1px !important", borderStyle: "solid !important" }, strategyBorderMutedSx]}>
              <Grid container sx={[strategyMutedValueSx, { fontSize: "0.875rem !important" }]}>
                <Grid
                  size={{ md: 6 }}

                  sx={{ display: 'flex', justifyContent: 'center' }}
                >
                  <div>
                    <Box sx={{ color: '#4f5062', pb: '0.25rem' }}>
                      Entry Fee
                    </Box>
                    <Box sx={[{ display: 'flex', alignItems: 'center', justifyContent: 'center', }, { fontSize: "1.15rem !important" }, strategyDarkTextSx]}>
                      <Box
                        sx={[strategyMinHeight50Sx, {
                          display: 'flex',
                          p: '0.25rem',
                          alignItems: 'center',
                        }]}
                      >
                        <Box
                          component="span"
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            whiteSpace: 'nowrap',
                            lineHeight: 1.3,
                          }}
                        >
                          <CountUp
                            start={0}
                            end={cashEntryFee}
                            duration={0}
                            separator=","
                            delay={1}
                            decimals={0}
                            decimal="."
                            prefix="$"
                          />
                        </Box>
                      </Box>
                    </Box>
                  </div>
                </Grid>
                <Grid
                  size={{ md: 6 }}

                  sx={{ display: 'flex', justifyContent: 'center' }}
                >
                  <div>
                    <Box sx={{ color: '#4f5062', pb: '0.25rem' }}>
                      Max Allowable Offer
                    </Box>
                    <Box sx={[{ display: 'flex', alignItems: 'center', justifyContent: 'center', }, { fontSize: "1.15rem !important" }, strategyDarkTextSx]}>
                      <Box
                        sx={[strategyMinHeight50Sx, {
                          display: 'flex',
                          p: '0.25rem',
                          alignItems: 'center',
                        }]}
                      >
                        <Box
                          component="span"
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            whiteSpace: 'nowrap',
                            lineHeight: 1.3,
                          }}
                        >
                          <CountUp
                            start={0}
                            end={
                              analysis.afterRepairValue - cashEntryFee
                            }
                            duration={0}
                            separator=","
                            delay={1}
                            decimals={0}
                            decimal="."
                            prefix="$"
                          />
                        </Box>
                      </Box>
                    </Box>
                  </div>
                </Grid>
              </Grid>
            </Box>
            <Grid size={12} sx={strategyMarginTop35Sx}>
              <Grid container direction="column" spacing={4}>
                <ContactInformation property={property} />
              </Grid>
            </Grid>
            <CommentsDialog property={property} userId={userId} />
            <Button
              variant="contained"
              color="primary"
              fullWidth
             
              onClick={(e) => setOpenPITICashData(true)} sx={strategyMarginTop25Sx}>
              <Typography>View Cash Underwriting</Typography>
            </Button>
          </>
        )}
        {dealSauceView === 'creative' && (
          <>
            <Box sx={[strategySummaryBandSx, { borderRadius: '0.65rem', p: '0.5rem', mb: '0.5rem', textAlign: 'center' }, { borderWidth: "1px !important", borderStyle: "solid !important" }, strategyBorderMutedSx]}>
              <Grid container sx={[strategyMutedValueSx, { fontSize: "0.875rem !important" }]}>
                <Grid
                  size={{ md: 6 }}

                  sx={{ display: 'flex', justifyContent: 'center' }}
                >
                  <div>
                    <Box sx={{ color: '#4f5062', pb: '0.25rem' }}>
                      Entry Fee
                    </Box>
                    <Box sx={[{ display: 'flex', alignItems: 'center', justifyContent: 'center', }, { fontSize: "1.15rem !important" }, strategyDarkTextSx]}>
                      <Box
                        sx={{
                          display: 'flex',
                          p: '0.25rem',
                          alignItems: 'center',
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            whiteSpace: 'nowrap',
                            lineHeight: 1.3,
                          }}
                        >
                          <CountUp
                            start={0}
                            end={creativeEntryFee}
                            duration={1}
                            separator=","
                            delay={1}
                            decimals={0}
                            decimal="."
                            prefix="$"
                          />
                        </Box>
                      </Box>
                    </Box>
                  </div>
                </Grid>
                <Grid
                  size={{ md: 6 }}

                  sx={{ display: 'flex', justifyContent: 'center' }}
                >
                  <div>
                    <Box sx={{ color: '#4f5062', pb: '0.25rem' }}>
                      PITI
                    </Box>
                    <Box sx={[{ display: 'flex', alignItems: 'center', justifyContent: 'center', }, { fontSize: "1.15rem !important" }, strategyDarkTextSx]}>
                      <Box
                        sx={{
                          display: 'flex',
                          p: '0.25rem',
                          alignItems: 'center',
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            whiteSpace: 'nowrap',
                            lineHeight: 1.3,
                          }}
                        >
                          <CountUp
                            start={0}
                            end={pitiTotal}
                            duration={1}
                            separator=","
                            delay={1}
                            decimals={0}
                            decimal="."
                            prefix="$"
                          />
                        </Box>
                      </Box>
                    </Box>
                  </div>
                </Grid>
                <Grid
                  size={{ md: 12 }}

                  sx={{ display: 'flex', justifyContent: 'center' }}
                >
                  <div>
                    <Box sx={{ color: '#4f5062', pb: '0.25rem' }}>
                      % of Purchase Price
                    </Box>
                    <Box sx={[{ display: 'flex', alignItems: 'center', justifyContent: 'center', }, { fontSize: "1.15rem !important" }, strategyDarkTextSx]}>
                      <Box
                        sx={{
                          display: 'flex',
                          p: '0.25rem',
                          alignItems: 'center',
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            whiteSpace: 'nowrap',
                            lineHeight: 1.3,
                          }}
                        >
                          <CountUp
                            start={0}
                            end={
                              analysis?.offerPrice &&
                              analysis.offerPrice !== 0
                                ? (creativeEntryFee /
                                    analysis.offerPrice) *
                                  100
                                : 0
                            }
                            duration={1}
                            delay={1}
                            decimals={2}
                            decimal="."
                            suffix="%"
                          />
                        </Box>
                      </Box>
                    </Box>
                  </div>
                </Grid>
              </Grid>
            </Box>
            <Grid container>
              <Grid size={12}>
                <Grid container direction="column" spacing={4}>
                  <ContactInformation property={property} />
                </Grid>
              </Grid>
              <CommentsDialog property={property} userId={userId} />
              <Button
                variant="contained"
                color="primary"
                fullWidth
               
                onClick={(e) => setOpenPITICreativeData(true)} sx={strategyMarginTop25Sx}>
                <Typography>View Creative Underwriting</Typography>
              </Button>
            </Grid>
          </>
        )}
      </CardContent>
      <SubtoPITICash
        open={openPITICashData}
        onClose={() => setOpenPITICashData(false)}
        propertyId={property?._id}
        address={property?.address}
        city={property.city}
        state={property.state}
        zipcode={property?.zip}
      />
      <SubtoPITICreative
        open={openPITICreativeData}
        onClose={() => setOpenPITICreativeData(false)}
        propertyId={property?._id}
        address={property?.address}
        city={property.city}
        state={property.state}
        zipcode={property?.zip}
      />
    </Card>
  )
}

const CardSummary = (props) => {
  const {
    property,
    dateCreatedString,
    analysis,
    analytics,
    currencyFormat,
    numberFormat,
    strategyCards,
    gorillaSplit,
    gorillaFinalFee,
    loading,
    setLoading,
    callPropertyExplorerReport,
    setOpenMemberDisclaimer,
    setOpenMember,
    toggleLimit,
    userId,
  } = props

  // Safely access activeOrgMember
  const activeOrgMember =
    props.props.session?.me?.activeOrg?.member || null

  const summaryDetails = [
    {
      name: 'Status',
      value: property.status,
      formatter: property.status || '-',
    },
    {
      name: 'Asking Price',
      value: analysis.listPrice,
      formatter: currencyFormat,
    },
    { name: 'Neighborhood', value: toUpper(analysis.neighborhood) },
    { name: 'MLS/Source ID', value: property.MLSID },
  ]

  const renderAction = () => {
    if (props.public || props.shared) return null

    if (activeOrgMember === 'gorillacapital') {
      const fixFlipRatio =
        (analytics.fixFlip.onTargetFlip +
          analytics.fixFlip.aboveTargetFlip) /
        (analytics.fixFlip.belowTargetFlip +
          analytics.fixFlip.onTargetFlip +
          analytics.fixFlip.aboveTargetFlip)

      return (
        <IconButton
          size="small" sx={{
            backgroundColor:
              fixFlipRatio >= 0.66
                ? '#3edf6e'
                : fixFlipRatio >= 0.33
                  ? '#ff9700'
                  : '#ff3f3d',
            color: 'white',
          }}>
          {fixFlipRatio >= 0.66 ? (
            <ArrowUpwardIcon />
          ) : fixFlipRatio >= 0.33 ? (
            <RemoveIcon />
          ) : (
            <ArrowDownwardIcon />
          )}
        </IconButton>
      )
    }

    return (
      <Tooltip title="Edit Summary Details">
        <Button
          onClick={() => props.toggleCenter('Edit')}
         
          sx={{
            height: "44px",
            width: "44px",
            lineHeight: "44px",
            backgroundColor: "rgb(91.7692307692%, 96.4494720965%, 99.838612368%) !important",
            "html[data-color-mode=\"dark\"] &": { backgroundColor: "#2a2a2a !important" },
            borderRadius: "0.65rem",
          }}
        >
          <FontAwesomeIcon icon={['fas', 'pen']} sx={[strategyLinkBlueSx, { fontSize: "1.15rem !important" }]}/>
        </Button>
      </Tooltip>
    )
  }

  const renderContent = () => {
    if (activeOrgMember === 'gorillacapital') {
      return (
        <GorillaDetails
          {...{
            analysis,
            analytics,
            currencyFormat,
            numberFormat,
            gorillaSplit,
            gorillaFinalFee,
          }}
        />
      )
    }

    if (
      activeOrgMember === 'pacemorby' ||
      activeOrgMember === 'astroflip'
    ) {
      return (
        <PITICard
          property={property}
          analysis={analysis}
          userId={userId}
        />
      )
      // const pitiTotal = currencyFormat.format(
      //   Number(property?.pitiData?.creative?.principal) +
      //     Number(property?.pitiData?.creative?.interest) +
      //     Number(property?.pitiData?.creative?.taxes) +
      //     Number(property?.pitiData?.creative?.insurance) || 0,
      // ) // Replace with actual value
      // const creativeEntryFee =
      //   Number(property?.pitiData?.creative?.moneyToSeller || 0) +
      //   Number(property?.pitiData?.creative?.arrears || 0) +
      //   Number(property?.pitiData?.creative?.agentFee || 0) +
      //   Number(property?.pitiData?.creative?.assignmentFee || 0) +
      //   Number(
      //     property?.pitiData?.creative
      //       ?.transactionCoordinatorExpenses || 0,
      //   ) +
      //   Number(property?.pitiData?.creative?.lendingExpenses || 0) +
      //   Number(property?.pitiData?.creative?.estRehab || 0) +
      //   Number(property?.pitiData?.creative?.furnishings || 0) +
      //   Number(property?.pitiData?.creative?.holdingCosts || 0)

      // const cashEntryFee =
      //   Number(property?.pitiData?.cash?.tcExpenses || 0) +
      //   Number(property?.pitiData?.cash?.assignmentToSelf || 0) +
      //   Number(property?.pitiData?.cash?.dispoExpenses || 0) +
      //   Number(property?.pitiData?.cash?.jvConnectorFee || 0) +
      //   Number(property?.pitiData?.cash?.closingCost || 0) +
      //   Number(property?.pitiData?.cash?.agentExpense || 0) +
      //   Number(property?.pitiData?.cash?.renovationExpenses || 0) +
      //   Number(property?.pitiData?.cash?.optionalExpense || 0)

      // return (
      //   <PacemorbyDetails
      //     pitiTotal={pitiTotal}
      //     creativeEntryFee={currencyFormat.format(creativeEntryFee)}
      //     cashEntryFee={currencyFormat.format(cashEntryFee)}
      //     percentageOfEntryFee={percentageFormat.format(
      //       creativeEntryFee / analysis.offerPrice || 0,
      //     )}
      //   />
      // )
    }

    return (
      <SummaryDetails
        {...{
          summaryDetails,
          strategyCards,
          property,
          preferences: props.preferences,
          currencyFormat,
          analysis,
        }}
      />
    )
  }

  if (activeOrgMember === 'pacemorby') {
    return (
      <Card variant="outlined" sx={[strategyPacemorbyCardSx, { p: '1.5rem' }]}>
        <div>
          <Typography
            variant="h6"
            sx={strategySemiboldMb20Sx}
          >
            Summary
          </Typography>
          {renderContent()}
        </div>
        <Grid
          container
          direction="column"
          spacing={1}
          sx={strategyFlexAutoTopSx}
        >
          {!props.shared &&
            activeOrgMember !== 'gorillacapital' &&
            activeOrgMember !== 'paulmccomas' && (
              <Grid>
                <ContactAgent property={property} propertySummary />
              </Grid>
            )}
          {property.realeflowData && (
            <Grid>
              <DisplayRealeflowData property={property} button />
            </Grid>
          )}
        </Grid>
      </Card>
    )
  }

  // Default content for other org members
  return (
    <Card variant="outlined" sx={[strategyOverflowVisibleCardSx, { p: '1.5rem', overflow: 'visible' }]}>
      <CardHeader
        title={
          <Typography variant="h6" sx={strategyFontSemiboldSx}>
            Summary
          </Typography>
        }
        subheader={
          <Typography sx={strategyMutedMetaSx}>
            Added by{' '}
            {property.createdBy
              ? `${property.createdBy.profile.firstname} ${property.createdBy.profile.lastname[0]}`
              : 'Unknown'}{' '}
            on {dateCreatedString}
          </Typography>
        }
        action={renderAction()}

        sx={{ p: 0 }}
      />
      {property.description && (
        <Box sx={[strategyWhiteSurfaceSx, { borderRadius: '0.65rem' }, { overflowX: "hidden", overflowY: "auto", height: 150 }]}>
          <Box sx={{
            position: "relative",
            height: "100%",
            maxHeight: "100%",
            overflowX: "hidden",
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
          }}>
            <FormattedDescription
              text={property.description}
              typographyProps={{
                className: 'font-size-md',
                sx: strategyDescriptionTextSx,
              }}
              sx={{ marginTop: '10px', '& p': { mb: 0 } }}
            />
          </Box>
        </Box>
      )}
      <Box sx={[{ mt: '1.5rem' }, { background: "#eeeff8", height: "1px", overflow: "hidden" }]} />
      {renderContent()}
      <Box sx={[{ mb: '1.5rem' }, { background: "#eeeff8", height: "1px", overflow: "hidden" }]} />
      <Grid container direction="column" spacing={1}>
        {!props.shared &&
          activeOrgMember !== 'gorillacapital' &&
          activeOrgMember !== 'paulmccomas' && (
            <Grid>
              <ContactAgent property={property} propertySummary />
            </Grid>
          )}
        {property.realeflowData && (
          <Grid>
            <DisplayRealeflowData property={property} button />
          </Grid>
        )}
        {!props.shared &&
          (activeOrgMember === 'roitk' ||
            activeOrgMember === 'dbi') && (
            <>
              <Grid>
                <Button fullWidth variant="contained" onClick={() => { setLoading(true); callPropertyExplorerReport({ variables: { propertyId: property._id, address: property.address, zipcode: padStart( truncate(property.zip, { length: 5, omission: '',  }), 5, '0', ), }, refetchQueries: [ { query: GET_PROPERTY, variables: { id: property._id }, }, ], }).then(() => { setLoading(false)  }) }} disabled={ property.houseCanaryPropertyExplorerLink || loading } sx={[{ fontWeight: 700, textTransform: 'uppercase' }, { fontSize: "0.875rem !important" }]}>
                  {'Property Explorer Report'}
                </Button>
              </Grid>
              {property.houseCanaryPropertyExplorerLink && (
                <Grid>
                  <a
                    href={property.houseCanaryPropertyExplorerLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View Property Explorer Report
                  </a>
                </Grid>
              )}
              <Backdrop
                open={loading} sx={strategyBackdropWhiteSx}>
                <CircularProgress color="inherit" />
              </Backdrop>
            </>
          )}
        {(activeOrgMember === 'gorillacapital' ||
          activeOrgMember === 'paulmccomas') && (
          <>
            {property.jv ? (
              <Card variant="outlined">
                <CardContent sx={{ px: '1.5rem', py: '1rem' }}>
                  <Box
                    sx={{
                      pb: '1rem',
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}
                  >
                    Property Submission Status
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                    }}
                  >
                    <Box component="span" sx={strategyWhiteBlueChipSx}>
                      {property.jvStatus}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ) : property.lender ? (
              <Card variant="outlined">
                <CardContent sx={{ px: '1.5rem', py: '1rem' }}>
                  <Box
                    sx={{
                      pb: '1rem',
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}
                  >
                    Property Submission Status
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                    }}
                  >
                    <Box component="span" sx={strategyWhiteBlueChipSx}>
                      {property.lenderStatus}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ) : (
              <Button
                fullWidth
               
                onClick={
                  props.fixFlipPercentage < 100
                    ? () => setOpenMemberDisclaimer(true)
                    : () => setOpenMember(true)
                }
              >
                <Box component="span" sx={strategyInlineFlexSx}>
                  <img
                    src="/images/white-gorilla-only.png"
                    alt="gorilla"
                    height={50}
                  />
                </Box>
                <Box component="span" sx={[strategyFont16Sx, {
                  display: "inline-block",
                  "&:not(:first-of-type)": { ml: "0.5rem", marginLeft: "0.5rem" },
                  "& + .btn-wrapper--icon": { ml: "0.5rem", marginLeft: "0.5rem" },
                }]}>
                  Submit to Gorilla
                </Box>
              </Button>
            )}
          </>
        )}
      </Grid>
    </Card>
  )
}

export default CardSummary
